import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import './DataPage.css';
import './ExameDetalhes.css';

function formatTempo(minutos) {
  if (minutos == null) return '-';
  if (minutos < 60) return `${minutos}min`;
  if (minutos < 1440) {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  const dias = Math.floor(minutos / 1440);
  const horasResto = Math.floor((minutos % 1440) / 60);
  return horasResto > 0 ? `${dias}d ${horasResto}h` : `${dias}d`;
}

const grupoConfig = {
  rapido: { label: 'Rapido', sublabel: '≤ 24 horas', color: '#2e7d32', bg: '#e8f5e9' },
  medio: { label: 'Medio', sublabel: '2-3 dias', color: '#1976d2', bg: '#e3f2fd' },
  demorado: { label: 'Demorado', sublabel: '4-7 dias', color: '#f57c00', bg: '#fff3e0' },
  muitoDemorado: { label: 'Muito Demorado', sublabel: '> 7 dias', color: '#c62828', bg: '#ffebee' }
};

const agrupamentoOpcoes = [
  { value: 'dia', label: 'Dia' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes', label: 'Mes' }
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      fontSize: '0.85rem'
    }}>
      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#333' }}>{d.label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div><span style={{ color: '#1976d2', fontWeight: '500' }}>Media:</span> {formatTempo(d.mediaMinutos)}</div>
        <div><span style={{ color: '#888' }}>Min:</span> {formatTempo(d.minMinutos)}</div>
        <div><span style={{ color: '#888' }}>Max:</span> {formatTempo(d.maxMinutos)}</div>
        <div><span style={{ color: '#888' }}>Amostras:</span> {d.amostras}</div>
      </div>
    </div>
  );
}

function TempoExameDetalhe() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agrupamento, setAgrupamento] = useState('semana');
  const [referencia, setReferencia] = useState('entrada');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    fetchData();
  }, [agrupamento, referencia]);

  const fetchData = async (inicio, fim) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('agrupamento', agrupamento);
      params.append('referencia', referencia);
      if (inicio || dataInicio) params.append('dataInicio', inicio || dataInicio);
      if (fim || dataFim) params.append('dataFim', fim || dataFim);
      const response = await axios.get(`${API_URL}/dashboard/tat/${encodeURIComponent(codigo)}?${params.toString()}`);
      setData(response.data);
      if (!inicio && !dataInicio && response.data.periodo) {
        setDataInicio(response.data.periodo.dataInicio);
        setDataFim(response.data.periodo.dataFim);
      }
    } catch (err) {
      console.error('Erro ao buscar detalhe TAT:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    fetchData(dataInicio, dataFim);
  };

  // Prazo nominal em minutos para ReferenceLine
  const prazoNominalMin = data?.exame?.diasEntregaNominal ? data.exame.diasEntregaNominal * 1440 : null;
  const cfg = data?.exame?.grupo ? grupoConfig[data.exame.grupo] : null;

  // Dados do gráfico com range min-max
  const chartData = data?.evolucao?.map(item => ({
    ...item,
    range: [item.minMinutos, item.maxMinutos]
  })) || [];

  // Calcular domínio do eixo Y
  const allValues = chartData.flatMap(d => [d.minMinutos, d.maxMinutos, d.mediaMinutos]);
  if (prazoNominalMin) allValues.push(prazoNominalMin);
  const yMax = allValues.length > 0 ? Math.max(...allValues) * 1.1 : 1000;

  // Formatar eixo Y em horas/dias
  const formatYAxis = (value) => {
    if (value < 60) return `${value}m`;
    if (value < 1440) return `${Math.round(value / 60)}h`;
    return `${(value / 1440).toFixed(1)}d`;
  };

  return (
    <div className="data-page" style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', fontFamily: 'Inter, Roboto, "Open Sans", sans-serif' }}>
      {/* Header */}
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/tempo-exames')} className="back-button">← Voltar</button>
        <h1>{data?.exame?.descricao || codigo}</h1>
        <p style={{ fontSize: '0.85rem', color: '#888', margin: 0 }}>
          Codigo: {codigo}
          {loading && <span style={{ marginLeft: '12px', color: '#1976d2' }}>Carregando...</span>}
        </p>
      </div>

      {/* Cards resumo */}
      {data && (
        <div style={styles.cardsRow}>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>Media Global</div>
            <div style={styles.cardValue}>{formatTempo(data.exame.mediaGlobalMinutos)}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>Prazo Nominal</div>
            <div style={styles.cardValue}>
              {data.exame.diasEntregaNominal != null ? `${data.exame.diasEntregaNominal}d` : '-'}
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>Total Amostras</div>
            <div style={styles.cardValue}>{data.exame.totalAmostras.toLocaleString('pt-BR')}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.cardLabel}>Grupo</div>
            <div style={styles.cardValue}>
              {cfg && (
                <span style={{
                  display: 'inline-block',
                  padding: '4px 14px',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: cfg.color,
                  backgroundColor: cfg.bg
                }}>
                  {cfg.label}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Controles */}
      <div style={styles.controlsRow}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={styles.toggleGroup}>
            {agrupamentoOpcoes.map(opt => (
              <button
                key={opt.value}
                onClick={() => setAgrupamento(opt.value)}
                style={{
                  ...styles.toggleBtn,
                  ...(agrupamento === opt.value ? styles.toggleBtnActive : {})
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={styles.toggleGroup}>
            <button
              onClick={() => setReferencia('entrada')}
              style={{
                ...styles.toggleBtn,
                ...(referencia === 'entrada' ? styles.toggleBtnActiveGreen : {})
              }}
            >
              Dt. Entrada
            </button>
            <button
              onClick={() => setReferencia('conclusao')}
              style={{
                ...styles.toggleBtn,
                borderRight: 'none',
                ...(referencia === 'conclusao' ? styles.toggleBtnActiveGreen : {})
              }}
            >
              Dt. Conclusao
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={dataInicio}
            onChange={e => setDataInicio(e.target.value)}
            style={styles.dateInput}
          />
          <input
            type="date"
            value={dataFim}
            onChange={e => setDataFim(e.target.value)}
            style={styles.dateInput}
          />
          <button onClick={handleBuscar} style={styles.btnBuscar}>Buscar</button>
        </div>
      </div>

      {/* Gráfico */}
      <div style={styles.chartContainer}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>Carregando grafico...</div>
        )}
        {!loading && chartData.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>Sem dados para o periodo selecionado</div>
        )}
        {!loading && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={380}>
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <defs>
                <linearGradient id="colorMedia" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorRange" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#9e9e9e" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#9e9e9e" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#888' }}
                tickLine={false}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fontSize: 12, fill: '#888' }}
                tickLine={false}
                axisLine={{ stroke: '#e0e0e0' }}
                domain={[0, yMax]}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* Range min-max */}
              <Area
                type="monotone"
                dataKey="maxMinutos"
                stroke="none"
                fill="url(#colorRange)"
                fillOpacity={1}
              />
              <Area
                type="monotone"
                dataKey="minMinutos"
                stroke="none"
                fill="#f5f5f5"
                fillOpacity={1}
              />
              {/* Linha média */}
              <Area
                type="monotone"
                dataKey="mediaMinutos"
                stroke="#1976d2"
                strokeWidth={2}
                fill="url(#colorMedia)"
                fillOpacity={1}
                dot={{ r: 3, fill: '#1976d2', strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#1976d2', stroke: '#fff', strokeWidth: 2 }}
              />
              {/* Linha prazo nominal */}
              {prazoNominalMin && (
                <ReferenceLine
                  y={prazoNominalMin}
                  stroke="#4caf50"
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  label={{
                    value: `Prazo: ${data.exame.diasEntregaNominal}d`,
                    position: 'right',
                    fill: '#4caf50',
                    fontSize: 12,
                    fontWeight: 500
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabela complementar */}
      {!loading && chartData.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Periodo</th>
                <th style={styles.th}>Media</th>
                <th style={styles.th}>Min</th>
                <th style={styles.th}>Max</th>
                <th style={styles.th}>Amostras</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((item, idx) => (
                <tr key={item.periodo} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={styles.td}>{item.label}</td>
                  <td style={styles.td}>
                    <span style={{
                      fontWeight: '600',
                      color: prazoNominalMin && item.mediaMinutos > prazoNominalMin ? '#c62828' : '#333'
                    }}>
                      {formatTempo(item.mediaMinutos)}
                    </span>
                  </td>
                  <td style={styles.td}>{formatTempo(item.minMinutos)}</td>
                  <td style={styles.td}>{formatTempo(item.maxMinutos)}</td>
                  <td style={styles.td}>{item.amostras.toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    textAlign: 'center'
  },
  cardLabel: { fontSize: '0.8rem', color: '#888', marginBottom: '8px', fontWeight: '500' },
  cardValue: { fontSize: '1.5rem', fontWeight: '700', color: '#333' },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px'
  },
  toggleGroup: {
    display: 'inline-flex',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid #ddd'
  },
  toggleBtn: {
    padding: '8px 18px',
    backgroundColor: '#fff',
    color: '#555',
    border: 'none',
    borderRight: '1px solid #ddd',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  toggleBtnActive: {
    backgroundColor: '#1976d2',
    color: '#fff'
  },
  toggleBtnActiveGreen: {
    backgroundColor: '#2e7d32',
    color: '#fff'
  },
  dateInput: {
    padding: '7px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.85rem',
    backgroundColor: '#fff'
  },
  btnBuscar: {
    padding: '8px 20px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer'
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '24px'
  },
  tableWrapper: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'auto'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#666',
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '10px 16px',
    textAlign: 'center',
    fontSize: '0.85rem',
    color: '#333',
    borderBottom: '1px solid #f0f0f0'
  }
};

export default TempoExameDetalhe;
